import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const displayValue = (value) => (value?.trim?.() ? value : '—')

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
        )
        .join(' ')
}

const formatPrice = (price) => {
    if (price == null || price === '') return '—'
    const num = Number(price)
    return Number.isNaN(num)
        ? price
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getTypeBadge = (type) => {
    switch (type?.toLowerCase()) {
        case 'product':
            return { cls: 'badge-soft-primary', label: 'Product' }
        case 'service':
            return { cls: 'badge-soft-success', label: 'Service' }
        default:
            return { cls: 'badge-soft-secondary', label: type ?? '—' }
    }
}

/* ─── component ───────────────────────────────────────────────────────────── */

const Products = () => {
    const [products, setProducts] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const fetchProducts = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await api.get('/products/')
            const data = res.data
            setProducts(Array.isArray(data) ? data : (data?.results ?? []))
        } catch (err) {
            console.error('Failed to fetch products', err)
            setError(getErrorMessage(err, 'Failed to load products. Please try again.'))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchProducts()
    }, [fetchProducts])

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">

                    {/* Page header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Products &amp; Services</h4>
                            <p className="text-muted mb-0">View and manage your product catalogue</p>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {!loading && !error && (
                                <span className="badge badge-soft-primary fs-13 px-3 py-2">
                                    {products.length} {products.length === 1 ? 'Item' : 'Items'}
                                </span>
                            )}
                            <Link to="/create-product" className="btn btn-primary">
                                <i className="ti ti-plus me-1" />
                                Add Product
                            </Link>
                        </div>
                    </div>

                    {/* Table card */}
                    <div className="card shadow-sm">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="fw-bold mb-0">All Products &amp; Services</h5>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-white"
                                onClick={fetchProducts}
                                disabled={loading}
                            >
                                <i className="ti ti-refresh me-1" />
                                Refresh
                            </button>
                        </div>

                        <div className="card-body p-0">

                            {/* Loading */}
                            {loading && (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <BeatLoader color="#3550DC" size={10} />
                                </div>
                            )}

                            {/* Error */}
                            {!loading && error && (
                                <div className="alert alert-danger m-3 mb-0" role="alert">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <span>
                                            <i className="ti ti-alert-circle me-2" />
                                            {error}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={fetchProducts}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Empty state */}
                            {!loading && !error && products.length === 0 && (
                                <div className="text-center py-5">
                                    <div className="avatar avatar-lg bg-light text-muted mx-auto mb-3">
                                        <i className="ti ti-package fs-24" />
                                    </div>
                                    <h6 className="fw-semibold mb-1">No products found</h6>
                                    <p className="text-muted mb-3">Products and services you add will appear here.</p>
                                    <Link to="/create-product" className="btn btn-primary">
                                        <i className="ti ti-plus me-1" />
                                        Add Product
                                    </Link>
                                </div>
                            )}

                            {/* Table */}
                            {!loading && !error && products.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-hover table-nowrap border mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Name</th>
                                                <th>Type</th>
                                                <th>Description</th>
                                                <th className="text-end">Unit Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.map((product, index) => {
                                                const { cls, label } = getTypeBadge(product.product_type)
                                                return (
                                                    <tr key={product.id ?? index}>
                                                        <td className="text-muted fs-12">{index + 1}</td>
                                                        <td>
                                                            <span className="fw-medium text-dark">
                                                                {displayValue(product.name)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${cls}`}>
                                                                {label}
                                                            </span>
                                                        </td>
                                                        <td className="text-muted" style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                                                            {displayValue(product.description)}
                                                        </td>
                                                        <td className="text-end fw-medium">
                                                            {formatPrice(product.unit_price)}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default Products